import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

export interface AlertItem {
  id: string;
  type: string; // severity: error|warning|info
  title: string;
  message: string;
  time: string; // 相对时间
  unread: boolean;
}

function relativeTime(iso: string): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return '刚刚';
  if (m < 60) return `${m}分钟前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}小时前`;
  return `${Math.floor(h / 24)}天前`;
}

/**
 * 系统告警/通知(GET /api/monitoring/alerts)。挂载 + 每 60s 轮询。
 * 桌面 Header 铃铛与移动端「通知」Tab 共用此 hook——单一数据源、避免重复轮询
 * (Layout 在移动/桌面两分支互斥渲染,任一时刻只有一处挂载,不会双轮询)。
 */
export function useAlerts(pollMs = 60000) {
  const [notifications, setNotifications] = useState<AlertItem[]>([]);

  useEffect(() => {
    let alive = true;
    const fetchAlerts = async () => {
      try {
        const res = await api.get('/monitoring/alerts', { params: { limit: 20 }, timeout: 10000 });
        const raw = res?.data?.data ?? res?.data ?? [];
        if (!alive || !Array.isArray(raw)) return;
        setNotifications(
          raw.map((a: Record<string, unknown>) => ({
            id: String(a.id),
            type: String(a.severity || 'info'),
            title: String(a.title || ''),
            message: String(a.message || ''),
            time: relativeTime(String(a.created_at || '')),
            unread: true,
          }))
        );
      } catch {
        /* 静默:通知非关键路径 */
      }
    };
    fetchAlerts();
    const timer = setInterval(fetchAlerts, pollMs);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [pollMs]);

  const markRead = useCallback(async (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false } : n)));
    try { await api.post(`/monitoring/alerts/${id}/read`, {}); } catch { /* ignore */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
    try { await api.post('/monitoring/alerts/read-all', {}); } catch { /* ignore */ }
  }, []);

  const unreadCount = notifications.filter((n) => n.unread).length;

  return { notifications, unreadCount, markRead, markAllRead };
}

export default useAlerts;
